import { useCallback, useEffect, useRef, useState } from 'react';
import { reportsApi, type ReportCounts } from './reports-api.js';
import { supportSession, supportToken } from './fleet-token.js';
import { usePoll } from './usePoll.js';

/**
 * The Reports nav badge (support#87): how many reports are active, and whether
 * a child-safety report is waiting for somebody to open it, which makes the
 * badge blink. Held by the app shell, so the nav and the Reports screen read
 * one poll.
 *
 * ── WHEN IT SIGNS IN, AND WHY THAT DIFFERS FROM CI FLEET ─────────────
 *
 * Getting a support token leaves the page for auth and comes back.
 * `useFleetAlerts` refuses to do that anywhere but CI fleet, and until it is
 * opened there is simply no count. That is right for a count of stuck CI jobs
 * and wrong here: the blink exists to say "a child may be at risk" to somebody
 * who has not gone looking (Jon, 2026-10-24). So this signs in at two moments
 * where the round trip costs nothing:
 *
 *   - once per tab, when the portal first loads, for a session that may work reports
 *     (`user:ban`), before anything has been typed;
 *   - on any Support screen, which signs in for its own data anyway.
 *
 * Everywhere else it uses only a token the page already holds, so reading
 * Users never reloads the page under somebody's cursor. When that token lapses
 * (15 minutes) the badge keeps its last answer until the next Support screen.
 * The email to jon@ridgewill.com is what covers a report nobody is looking for.
 */
export function useReportCounts(enabled: boolean, onSupportScreen: boolean): ReportCounts | null {
  const [counts, setCounts] = useState<ReportCounts | null>(null);
  /** A ref, not state: flipping it must not re-run the effect and fetch twice. */
  const askedThisLoad = useRef(false);

  const reload = useCallback(async () => {
    if (!enabled) return;
    let mayRedirect = onSupportScreen;
    if (!mayRedirect && !askedThisLoad.current) {
      askedThisLoad.current = true;
      mayRedirect = claimFirstLoadSignIn(sessionStore());
    }
    if (!mayRedirect && !supportSession.peek()) return;
    try {
      setCounts(await reportsApi(supportToken).counts());
    } catch {
      // A count that could not be read keeps the last one; the Reports screen says why.
    }
  }, [enabled, onSupportScreen]);

  useEffect(() => {
    void reload();
  }, [reload]);
  usePoll(useCallback(() => void reload(), [reload]));

  return enabled ? counts : null;
}

export const FIRST_LOAD_KEY = 'rw-admin-support-first-load-signed-in';

/**
 * Whether this is the tab's first portal load, claimed so it is asked once.
 *
 * "Once, when the portal first loads" has to survive the round trip itself.
 * Every sign-in reloads the page, and a flag kept in memory came back false
 * after each one: on CI fleet the support sign-in reloaded the page, which
 * dropped the fleet token, whose renewal reloaded the page, which signed in to
 * support again, until auth answered 429. So the claim lives in the tab's
 * session storage, which the round trip keeps. Blocked storage claims nothing:
 * signing in needs it anyway.
 */
export function claimFirstLoadSignIn(store: Storage | undefined): boolean {
  if (!store) return false;
  try {
    if (store.getItem(FIRST_LOAD_KEY)) return false;
    store.setItem(FIRST_LOAD_KEY, '1');
    return true;
  } catch {
    return false;
  }
}

function sessionStore(): Storage | undefined {
  try {
    return window.sessionStorage;
  } catch {
    return undefined;
  }
}

/** The badge blinks while either list holds an unopened child-safety report. */
export function needsAttention(counts: ReportCounts | null): boolean {
  return Boolean(counts && counts.unviewedChildSafety.new + counts.unviewedChildSafety.escalated > 0);
}
